import { api, logout } from "../api/client.ts";
import type { Me } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { download } from "../lib/data.ts";
import { spaceUrl } from "../lib/paths.ts";
import { useAction } from "../lib/resource.ts";
import { atLeast } from "../lib/roles.ts";
import { me } from "../lib/state.ts";
import { Button, Link } from "../ui/Button.tsx";
import { ConfirmButton } from "../ui/Dialog.tsx";
import { Section } from "../ui/misc.tsx";
import { RowList } from "../ui/RowList.tsx";
import { buttonClass } from "../ui/styles.ts";
import { AccountTabs } from "./account/tabs.tsx";
import { MySessions } from "./settings/activity.tsx";

/** Where you're signed in, and the tools that used your keys. */
export function AccountSessions() {
  return (
    <>
      <AccountTabs current="/profile/sessions" />
      <p class="mb-4 text-sm text-zinc-600">{t("profile.sessionsHint")}</p>
      <MySessions />
    </>
  );
}

/** Your privacy: what Revisa keeps about you (yours to download or delete), and what organizations keep (theirs). */
export function AccountPrivacy() {
  const u = me.value as Me;
  const [exportMe, exporting] = useAction(() => download("/me/export", "revisa-me.json"));
  const [remove, removing] = useAction(async () => {
    await api.del("/me");
    await logout();
  });
  const admin = u.orgs.filter((o) => atLeast(o.role, "admin") && o.spaces[0]);
  return (
    <>
      <AccountTabs current="/profile/privacy" />
      <Section title={t("account.personal")}>
        <p class="mb-4 text-sm text-zinc-700">{t("account.personalHint")}</p>
        <div class="flex flex-wrap gap-3">
          <Button busy={exporting} onClick={() => exportMe()}>
            {t("profile.export")}
          </Button>
          <ConfirmButton
            variant="danger"
            label={t("profile.delete")}
            title={t("profile.delete")}
            message={t("profile.deleteMessage")}
            confirmLabel={t("profile.delete")}
            typeToConfirm={u.email ?? u.name}
            busy={removing}
            onConfirm={remove}
          />
        </div>
      </Section>
      <Section title={t("account.orgPrivacy")}>
        <p class="mb-4 text-sm text-zinc-700">{t("account.orgPrivacyHint")}</p>
        {admin.length > 0 ? (
          <>
            <p class="mb-3 text-sm text-zinc-600">{t("account.orgPrivacyAdmin")}</p>
            <RowList
              rows={admin.map((o) => ({
                key: o.org,
                title: o.name,
                detail: t(`role.${o.role}`),
                aside: (
                  <Link
                    href={spaceUrl({ id: o.spaces[0]?.space ?? "" }, "/settings/privacy")}
                    class={buttonClass("secondary", "sm")}
                  >
                    {t("account.orgPrivacyLink")}
                  </Link>
                ),
              }))}
            />
          </>
        ) : null}
      </Section>
    </>
  );
}
